"use client";

import React, { useEffect, useRef, useState } from "react";
import { BarChart3, Mail, Package, Settings, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const usages = [{
  title: "Transaction Integrity",
  description: "Rigorous verification protocols to ensure secure processing of capital allocations and distributions.",
  icon: Package
}, {
  title: "Strategic Insights",
  description: "Utilization of anonymized datasets to refine market analysis and optimize portfolio performance.",
  icon: BarChart3
}, {
  title: "Regulatory Compliance",
  description: "Maintaining global standards for KYC, AML, and institutional reporting with absolute precision.",
  icon: Settings
}, {
  title: "Infrastructure Security",
  description: "Multi-layered encryption and secure handling protocols to safeguard institutional-grade assets.",
  icon: ShieldCheck
}, {
  title: "Verified Communication",
  description: "Direct delivery of performance reports and critical updates through encrypted, verified channels.",
  icon: Mail
}];
export default function HowWeUseYourData() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="190529" id="how-we-use-your-data" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-center transition-all duration-[800ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
          {/* Image Side */}
          <div className="lg:col-span-8 lg:col-start-1 lg:row-start-1 relative rounded-2xl overflow-hidden shadow-2xl">
            <AspectRatio ratio={16 / 10} className="w-full h-full lg:h-[650px]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/carbon_fiber_macro_texture.png" alt="Institutional grade secure infrastructure texture" className="object-cover w-full h-full" />
              <div className="absolute inset-0 bg-gradient-to-r from-secondary/30 to-secondary/60 mix-blend-multiply pointer-events-none"></div>
            </AspectRatio>
          </div>

          {/* Content Card */}
          <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1 z-10 lg:-ml-8 mt-[-4rem] lg:mt-0 relative">
            <Card className="bg-card/98 backdrop-blur-2xl border-border/60 shadow-2xl rounded-xl overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-primary"></div>
              <CardHeader className="pb-6 pt-10 px-8 md:px-12">
                <CardTitle className="text-3xl md:text-4xl font-bold tracking-tight text-secondary font-default">
                  Data Governance & Processing
                </CardTitle>
              </CardHeader>
              <CardContent className="px-8 md:px-12 pb-12">
                <ul className="space-y-8">
                  {usages.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-6 group">
                      <div className="mt-1 bg-muted p-3 rounded-lg text-secondary border border-border shadow-sm transition-all duration-300 group-hover:bg-chart-3/10 group-hover:text-chart-3 group-hover:border-chart-3/30 shrink-0">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-base md:text-lg font-semibold text-secondary tracking-tight mb-1.5 font-default">
                          {item.title}
                        </h4>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-sans">
                          {item.description}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}